import React from 'react';

import IconButtonLink from '@dashboard/components/mui-atoms/IconButtonLink';
import {
  type FPlatformFragment,
  usePlatformUsersQuery,
  WaypointFeatureTier,
} from '@dashboard/generated/wp/authentication';
import { getPlatformHref } from '@dashboard/hooks/navigate/usePlatformNavigator';
import { SettingsOutlined } from '@mui/icons-material';
import { Box, Chip, Skeleton, Stack, Tooltip, Typography } from '@mui/material';

import { NumberFormatter } from '@waypoint/react/formatters';

import TileLink from './TileLink';

type MembersCountProps = {
  platformId: string;
};
function MembersCount({ platformId }: MembersCountProps): JsxNode {
  const { data } = usePlatformUsersQuery({ variables: { platformId } });
  const count = data?.Platform.platformUsers.count;

  if (count === undefined) {
    return <Skeleton width={133} />;
  }
  if (count === 1) {
    return <>1 team member</>;
  }
  return (
    <>
      <NumberFormatter value={count} /> team members
    </>
  );
}

type Props = {
  platform: FPlatformFragment;
};
export default function PlatformTileLink({ platform }: Props) {
  const renderDescription = () => {
    if (typeof platform.description !== 'string') {
      return null;
    }
    if (platform.description.trim().length === 0) {
      return null;
    }
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          maxHeight: 36,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {platform.description}
      </Typography>
    );
  };

  const sx = { fontSize: 11 };
  const renderTier = () => {
    switch (platform.activeWaypointTier) {
      case WaypointFeatureTier.Free:
        return <Chip color="primary" sx={sx} size="small" label={platform.activeWaypointTier} />;
      case WaypointFeatureTier.Starter:
      case WaypointFeatureTier.Growth:
      case WaypointFeatureTier.Pro:
        return (
          <Chip
            size="small"
            color="primary"
            sx={{ bgcolor: 'brand.green', ...sx }}
            label={platform.activeWaypointTier}
          />
        );
      case WaypointFeatureTier.Unlimited:
        return (
          <Chip size="small" color="primary" sx={{ bgcolor: 'brand.red', ...sx }} label={platform.activeWaypointTier} />
        );
      case WaypointFeatureTier.Sample:
      case WaypointFeatureTier.Sandbox:
        return <Chip size="small" sx={sx} label={platform.activeWaypointTier} />;
    }
  };

  return (
    <TileLink href={getPlatformHref(platform.id)}>
      <Stack justifyContent="space-between" spacing={2} sx={{ height: '100%' }}>
        <Stack spacing={1}>
          <Typography noWrap fontWeight="medium">
            {platform.name}
          </Typography>
          {renderDescription()}
          <Typography variant="body2" color="text.secondary">
            <MembersCount platformId={platform.id} />
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Box sx={{ '.MuiChip-root': { cursor: 'pointer' } }}>{renderTier()}</Box>
          <IconButtonLink size="small" href={`/o/${platform.id}/settings`} color="secondary">
            <Tooltip title="Workspace settings">
              <SettingsOutlined fontSize="small" />
            </Tooltip>
          </IconButtonLink>
        </Stack>
      </Stack>
    </TileLink>
  );
}
